import React from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import {
  computeTC6PrimaryReference,
  type Tc6AttemptPayload,
  type Tc6ReferenceKey,
} from '../features/assessments-v2/tc6/tc6Domain';

interface TC6ComparisonProps {
  previous: Tc6AttemptPayload;
  current: Tc6AttemptPayload;
  previousDate?: string | null;
  currentDate?: string | null;
  previousReferenceKey?: Tc6ReferenceKey;
  currentReferenceKey?: Tc6ReferenceKey;
}

const toFiniteNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatClinicalDate = (value?: string | null): string => {
  if (!value) return 'data não informada';

  const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'data não informada' : parsed.toLocaleDateString('pt-BR');
};

const formatNumber = (value: number | null, decimals = 0): string => {
  if (value == null) return '—';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const getAttemptValues = (attempt: Tc6AttemptPayload, key: 'hr' | 'spo2'): number[] =>
  [
    toFiniteNumber(attempt.baseline?.[key]),
    ...(attempt.per_minute ?? []).map((entry) => toFiniteNumber(entry?.[key])),
    toFiniteNumber(attempt.final?.[key]),
  ].filter((value): value is number => value != null);

const getAttemptMetrics = (attempt: Tc6AttemptPayload, referenceKey?: Tc6ReferenceKey) => {
  const hrValues = getAttemptValues(attempt, 'hr');
  const spo2Values = getAttemptValues(attempt, 'spo2');
  const reference = computeTC6PrimaryReference(attempt, referenceKey);

  return {
    distance: toFiniteNumber(attempt.total_distance_m),
    percentPredicted: reference?.percentPredicted ?? toFiniteNumber(attempt.derivedMetrics?.percentPredicted),
    spo2Min: spo2Values.length ? Math.min(...spo2Values) : null,
    spo2Max: spo2Values.length ? Math.max(...spo2Values) : null,
    hrMin: hrValues.length ? Math.min(...hrValues) : null,
    hrMax: hrValues.length ? Math.max(...hrValues) : null,
    stops: attempt.stop_report?.intervals?.length ?? 0,
    stoppedSeconds: toFiniteNumber(attempt.stop_report?.total_stopped_s) ?? 0,
  };
};

export function TC6Comparison({
  previous,
  current,
  previousDate,
  currentDate,
  previousReferenceKey,
  currentReferenceKey,
}: TC6ComparisonProps) {
  const previousMetrics = getAttemptMetrics(previous, previousReferenceKey);
  const currentMetrics = getAttemptMetrics(current, currentReferenceKey);

  const renderTrend = (
    previousValue: number | null,
    currentValue: number | null,
    options?: { inverseLogic?: boolean; decimals?: number; suffix?: string },
  ) => {
    if (previousValue == null || currentValue == null) {
      return <span className="font-medium text-gray-400">—</span>;
    }

    const diff = currentValue - previousValue;
    const decimals = options?.decimals ?? 0;
    const suffix = options?.suffix ?? '';

    if (Math.abs(diff) < 10 ** -decimals / 2) {
      return (
        <span className="flex items-center font-medium text-gray-500">
          <Minus className="mr-1 h-4 w-4" /> Estável
        </span>
      );
    }

    const isPositiveEvolution = options?.inverseLogic ? diff < 0 : diff > 0;
    const Icon = diff > 0 ? ArrowUpRight : ArrowDownRight;

    return (
      <span className={`flex items-center font-semibold ${isPositiveEvolution ? 'text-green-600' : 'text-red-600'}`}>
        <Icon className="mr-1 h-4 w-4" />
        {diff > 0 ? '+' : '-'}{formatNumber(Math.abs(diff), decimals)}{suffix}
      </span>
    );
  };

  const previousDateLabel = formatClinicalDate(previousDate ?? previous.finishedAt);
  const currentDateLabel = formatClinicalDate(currentDate ?? current.finishedAt);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-700">
          Comparativo: Teste de Caminhada de 6 Minutos (TC6)
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm text-gray-600">
          <thead className="border-b border-gray-200 bg-gray-50 text-gray-500">
            <tr>
              <th className="px-4 py-3 font-medium">Parâmetro</th>
              <th className="px-4 py-3 font-medium">Anterior ({previousDateLabel})</th>
              <th className="px-4 py-3 font-medium">Atual ({currentDateLabel})</th>
              <th className="px-4 py-3 font-medium">Evolução Clínica</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr className="bg-gray-50/50">
              <td colSpan={4} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Desempenho</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium text-gray-700">Distância Percorrida</td>
              <td className="px-4 py-3">{formatNumber(previousMetrics.distance)} m</td>
              <td className="px-4 py-3 font-semibold text-gray-900">{formatNumber(currentMetrics.distance)} m</td>
              <td className="px-4 py-3">{renderTrend(previousMetrics.distance, currentMetrics.distance, { suffix: ' m' })}</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium text-gray-700">% do Predito</td>
              <td className="px-4 py-3">{formatNumber(previousMetrics.percentPredicted, 1)}%</td>
              <td className="px-4 py-3 font-semibold text-gray-900">{formatNumber(currentMetrics.percentPredicted, 1)}%</td>
              <td className="px-4 py-3">{renderTrend(previousMetrics.percentPredicted, currentMetrics.percentPredicted, { decimals: 1, suffix: '%' })}</td>
            </tr>

            <tr className="bg-gray-50/50">
              <td colSpan={4} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Sinais Vitais</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium text-gray-700">SpO2 (Mín - Máx)</td>
              <td className="px-4 py-3">{formatNumber(previousMetrics.spo2Min, 1)}% - {formatNumber(previousMetrics.spo2Max, 1)}%</td>
              <td className="px-4 py-3 font-semibold text-gray-900">{formatNumber(currentMetrics.spo2Min, 1)}% - {formatNumber(currentMetrics.spo2Max, 1)}%</td>
              <td className="px-4 py-3">
                <div className="flex items-center">
                  {renderTrend(previousMetrics.spo2Min, currentMetrics.spo2Min, { decimals: 1, suffix: '%' })}
                  <span className="ml-1 text-xs text-gray-400">(Mín)</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium text-gray-700">Freq. Cardíaca (Mín - Máx)</td>
              <td className="px-4 py-3">{formatNumber(previousMetrics.hrMin)} - {formatNumber(previousMetrics.hrMax)} bpm</td>
              <td className="px-4 py-3 font-semibold text-gray-900">{formatNumber(currentMetrics.hrMin)} - {formatNumber(currentMetrics.hrMax)} bpm</td>
              <td className="px-4 py-3">
                <div className="flex items-center">
                  {renderTrend(previousMetrics.hrMax, currentMetrics.hrMax, { inverseLogic: true, suffix: ' bpm' })}
                  <span className="ml-1 text-xs text-gray-400">(Máx)</span>
                </div>
              </td>
            </tr>

            <tr className="bg-gray-50/50">
              <td colSpan={4} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Intercorrências</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium text-gray-700">Número de Paradas</td>
              <td className="px-4 py-3">{previousMetrics.stops}</td>
              <td className="px-4 py-3 font-semibold text-gray-900">{currentMetrics.stops}</td>
              <td className="px-4 py-3">{renderTrend(previousMetrics.stops, currentMetrics.stops, { inverseLogic: true })}</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium text-gray-700">Tempo de Paradas</td>
              <td className="px-4 py-3">{formatNumber(previousMetrics.stoppedSeconds)} s</td>
              <td className="px-4 py-3 font-semibold text-gray-900">{formatNumber(currentMetrics.stoppedSeconds)} s</td>
              <td className="px-4 py-3">{renderTrend(previousMetrics.stoppedSeconds, currentMetrics.stoppedSeconds, { inverseLogic: true, suffix: ' s' })}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
